/**
 * WebView Navigation Middleware
 * 
 * Handles WebView-specific navigation issues, particularly around URL encoding
 * of dashboard routes which can cause 404 errors.
 * 
 * Common issues:
 * - Encoded slashes (%2F) in dashboard URLs
 * - Double-encoded paths in Android WebView
 * - Direct navigation to /account/dashboard failing in WebView
 */

import { defineNuxtRouteMiddleware, navigateTo } from '#app'

export default defineNuxtRouteMiddleware((to, from) => {
  // Only run this middleware in client-side environments
  if (process.server) return;
  
  // Generate a unique request ID for logs
  const requestId = Math.random().toString(36).substring(2, 8);
  
  // Helper function to detect WebView environments
  const isWebView = () => {
    if (!process.client) return false;
    
    const ua = navigator.userAgent.toLowerCase();
    return (
      ua.includes('webview') || 
      ua.includes('wv') ||
      // Android WebView specific
      (ua.includes('android') && ua.includes('version/'))
    );
  };
  
  // Check if we're in a WebView environment
  const webViewDetected = isWebView();
  
  // Enhanced logging for diagnostic purposes
  console.log(`[WebViewNavigation:${requestId}] Route changed: ${from.path} → ${to.path}`);
  console.log(`[WebViewNavigation:${requestId}] WebView detected: ${webViewDetected}`);
  
  // Special handling for dashboard routes
  if (to.path.includes('account/dashboard') || 
      to.path.includes('%2Faccount%2Fdashboard') ||
      to.path.includes('%252Faccount%252Fdashboard')) {
    
    console.log(`[WebViewNavigation:${requestId}] Dashboard route detected: ${to.path}`);
    
    // Normalize encoded dashboard paths
    const dashboardPath = '/account/dashboard';
    
    // Get query parameters from original route
    const query = { ...to.query };
    
    // Add default 'view=my-plans' if no view parameter is present
    if (!query.view) {
      console.log(`[WebViewNavigation:${requestId}] Adding default view=my-plans parameter`);
      query.view = 'my-plans';
    } else {
      console.log(`[WebViewNavigation:${requestId}] Preserving view parameter: ${query.view}`);
    }
    
    // Only redirect if the path is different from the normalized one
    // or if we needed to add the default view parameter
    if (to.path !== dashboardPath || !to.query.view) {
      console.log(`[WebViewNavigation:${requestId}] Redirecting to normalized dashboard path: ${dashboardPath}?view=${query.view}`);
      
      // Perform the redirect
      return navigateTo({
        path: dashboardPath,
        query
      }, { replace: true });
    }
  }
});
